import i18next, { type TFunction } from 'i18next'
import { initReactI18next, type TFunction as ReactTFunction, Trans } from 'react-i18next'
import { supportedLanguages, type SupportedLanguage } from '../../shared/i18n/i18n.no-deps'
import { loadSystemTranslations, type ESystem } from '../../shared/i18n/i18next-source-files'

export const DEFAULT_LANGUAGE = 'en-GB'

export class Translations {
    private static instance: Translations
    private static system: ESystem
    public static t: TFunction
    public static Trans: ReactTFunction
    public static language: SupportedLanguage
    public static fixed: (
        lng: string | readonly string[],
        ns?: string | readonly string[],
        keyPrefix?: string
    ) => TFunction

    private constructor() {
        if (typeof window !== 'undefined') {
            Translations.language = isSupportedLanguage(window.navigator.language) // bc of node
                ? window.navigator.language
                : DEFAULT_LANGUAGE
        }
    }

    private static async initI18n() {
        const resources = await loadSystemTranslations(this.system)

        const t = await i18next.use(initReactI18next).init({
            fallbackLng: DEFAULT_LANGUAGE,
            lng: DEFAULT_LANGUAGE,
            interpolation: {
                escapeValue: false, // not needed for react as it escapes by default
            },
            returnEmptyString: false,
            resources,
            returnObjects: true,
        })

        this.fixed = i18next.getFixedT

        return t
    }

    public static async init(system: ESystem): Promise<Translations> {
        this.system = system

        if (!Translations.instance) {
            this.t = await this.initI18n()
            this.Trans = Trans

            Translations.instance = new Translations()
        }

        return Translations.instance
    }

    public static async changeLanguage(lang: SupportedLanguage) {
        this.t = await i18next.changeLanguage(lang)
        this.language = lang
    }
}

function isSupportedLanguage(language: string): language is SupportedLanguage {
    return supportedLanguages.includes(language as SupportedLanguage)
}
